/**
 * /payroll/pay-runs/:payRunId — Pay Run Detail
 *
 * Header: run info + stat cards (Gross / EE Taxes / Net / ER Cost)
 * Actions: Calculate | Approve | Generate ACH | Post to GL | Void
 * Tab "Earnings": per-employee earnings entry grid
 * Tab "Summary": totals + paychecks list with stub download
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalculateIcon from "@mui/icons-material/Calculate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PostAddIcon from "@mui/icons-material/PostAdd";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PrintIcon from "@mui/icons-material/Print";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { api, getToken, errorMessage } from "@/lib/api";
import { MoneyField } from "@/components/MoneyField";
import { formatMoney } from "@/lib/money";
import {
  payRunQueryOptions,
  payrollEmployeesQueryOptions,
  earningCodesQueryOptions,
  paychecksQueryOptions,
  payrollKeys,
  myEntitiesQueryOptions,
} from "@/lib/queries";
import type {
  PayRun, PayRunStatus, PayrollEmployee, Paycheck,
  BurdenVarianceRow, OTCalculationLog, AllowanceEntry,
  ComplianceGateResult, EarnedValueSnapshot, AiLaborAnomaly,
} from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/pay-runs_/$payRunId")({
  component: PayRunDetailPage,
});

const STATUS_COLOR: Record<PayRunStatus, "default" | "primary" | "warning" | "success" | "error" | "info"> = {
  draft: "default", calculated: "primary", approved: "warning", paid: "info", posted: "success", voided: "error",
};
const STATUS_LABEL: Record<PayRunStatus, string> = {
  draft: "Draft", calculated: "Calculated", approved: "Approved", paid: "Paid", posted: "Posted", voided: "Voided",
};

function fmtDate(s: string) {
  return new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 120 }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h6" fontWeight={600} color={color}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

// ─── ACH Dialog ───────────────────────────────────────────────────────────────

interface AchFormState {
  company_name: string;
  company_id: string;
  immediate_origin: string;
  immediate_origin_name: string;
  odfi_routing: string;
  odfi_name: string;
  file_id_modifier: string;
  mark_sent: boolean;
}

function AchDialog({
  open, onClose, payRunId,
}: { open: boolean; onClose: () => void; payRunId: string }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<AchFormState>({
    company_name: "", company_id: "", immediate_origin: "", immediate_origin_name: "",
    odfi_routing: "", odfi_name: "", file_id_modifier: "A", mark_sent: true,
  });
  const [fileText, setFileText] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api<{ file_text: string; entry_count: number; total_amount: string; skipped: unknown[] }>(
        `/api/payroll/pay-runs/${payRunId}/ach`,
        { method: "POST", body: form }
      ),
    onSuccess: (data) => {
      setFileText(data.file_text);
      qc.invalidateQueries({ queryKey: payrollKeys.payRun(payRunId) });
    },
  });

  const downloadFile = () => {
    if (!fileText) return;
    const blob = new Blob([fileText], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll_${payRunId}.ach`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const set = (k: keyof AchFormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(prev => ({ ...prev, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate NACHA ACH File</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        {fileText ? (
          <>
            <Alert severity="success" sx={{ mb: 1 }}>
              ACH file generated — {fileText.split("\n").length} records.
            </Alert>
            <Button variant="contained" startIcon={<AccountBalanceIcon />} onClick={downloadFile}>
              Download .ach File
            </Button>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              Enter your company's ODFI (bank) originator details. These come from your bank's ACH origination agreement.
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField label="Company Name" value={form.company_name} onChange={set("company_name")} required />
              <TextField label="Company ID (10 digits)" value={form.company_id} onChange={set("company_id")} required />
              <TextField label="Immediate Origin (routing or TIN)" value={form.immediate_origin} onChange={set("immediate_origin")} required />
              <TextField label="Immediate Origin Name" value={form.immediate_origin_name} onChange={set("immediate_origin_name")} required />
              <TextField label="ODFI Routing Number" value={form.odfi_routing} onChange={set("odfi_routing")} required />
              <TextField label="ODFI Name" value={form.odfi_name} onChange={set("odfi_name")} required />
              <TextField label="File ID Modifier" value={form.file_id_modifier} onChange={set("file_id_modifier")} />
            </Box>
            <FormControlLabel
              control={<Switch checked={form.mark_sent} onChange={(e) => setForm(p => ({ ...p, mark_sent: e.target.checked }))} />}
              label="Mark paychecks as 'sent' after generating"
            />
            {mut.isError && <Alert severity="error">{String((mut.error as Error)?.message)}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{fileText ? "Close" : "Cancel"}</Button>
        {!fileText && (
          <Button
            variant="contained"
            onClick={() => mut.mutate()}
            disabled={mut.isPending || !form.company_name || !form.odfi_routing}
            startIcon={mut.isPending ? <CircularProgress size={16} /> : <AccountBalanceIcon />}
          >
            {mut.isPending ? "Generating…" : "Generate .ach File"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Post to GL Dialog ────────────────────────────────────────────────────────

function PostToGLDialog({
  open, onClose, payRunId,
}: { open: boolean; onClose: () => void; payRunId: string }) {
  const qc = useQueryClient();

  const mut = useMutation({
    mutationFn: () =>
      api<{ journal_no: string; total_debit: string; line_count: number }>(
        `/api/payroll/pay-runs/${payRunId}/post-to-gl`,
        { method: "POST", body: {} }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payrollKeys.payRun(payRunId) });
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Post Pay Run to GL</DialogTitle>
      <DialogContent sx={{ mt: 1 }}>
        {mut.isSuccess ? (
          <Alert severity="success">
            Journal #{mut.data.journal_no} posted — {formatMoney(mut.data.total_debit)} balanced across {mut.data.line_count} lines.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" mb={2}>
              This will post a balanced journal entry to the general ledger:
            </Typography>
            <Table size="small">
              <TableBody>
                {[
                  ["Dr 6050", "Salaries & Wages (gross)"],
                  ["Dr 6055–6058", "Employer taxes (FICA / FUTA / SUI / WC)"],
                  ["Cr 2140", "Payroll Liabilities (EE deductions + ER accruals)"],
                  ["Cr 1120", "Cash – Payroll (net pay)"],
                ].map(([acct, desc]) => (
                  <TableRow key={acct}>
                    <TableCell sx={{ border: 0, fontFamily: "monospace", whiteSpace: "nowrap", pr: 2 }}>{acct}</TableCell>
                    <TableCell sx={{ border: 0, color: "text.secondary" }}>{desc}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {mut.isError && <Alert severity="error" sx={{ mt: 2 }}>{String((mut.error as Error)?.message)}</Alert>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{mut.isSuccess ? "Close" : "Cancel"}</Button>
        {!mut.isSuccess && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => mut.mutate()}
            disabled={mut.isPending}
            startIcon={mut.isPending ? <CircularProgress size={16} /> : <PostAddIcon />}
          >
            {mut.isPending ? "Posting…" : "Post to GL"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ─── Earnings entry tab ───────────────────────────────────────────────────────

interface EarningLine {
  code: string;
  hours: string;
  rate: string;
}

interface EmployeeEntry {
  employee_id: string;
  employee_name: string;
  earnings: EarningLine[];
}

function EarningsEntryTab({ payRun, employees, earningCodes, complianceBlocked = false, onCalculateBlocked }: {
  payRun: PayRun;
  employees: PayrollEmployee[];
  earningCodes: { id: string; code: string; name: string }[];
  complianceBlocked?: boolean;
  onCalculateBlocked?: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<EmployeeEntry[]>(() =>
    employees.map(emp => ({
      employee_id: emp.id,
      employee_name: `${emp.first_name} ${emp.last_name}`,
      earnings: [{ code: "REG", hours: "80", rate: emp.default_rate }],
    }))
  );
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");

  const calcMut = useMutation({
    mutationFn: () =>
      api<PayRun>(`/api/payroll/pay-runs/${payRun.id}/calculate`, {
        method: "POST",
        body: {
          earnings: entries.map(e => ({
            employee_id: e.employee_id,
            earnings: e.earnings
              .filter(ln => ln.hours || ln.rate)
              .map(ln => ({
                code: ln.code,
                hours: parseFloat(ln.hours) || 0,
                rate: parseFloat(ln.rate) || 0,
                amount: (parseFloat(ln.hours) || 0) * (parseFloat(ln.rate) || 0),
              })),
          })).filter(e => e.earnings.length > 0),
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: payrollKeys.payRun(payRun.id) }),
  });

  const approveMut = useMutation({
    mutationFn: () => api<PayRun>(`/api/payroll/pay-runs/${payRun.id}/approve`, { method: "POST", body: {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: payrollKeys.payRun(payRun.id) }),
  });

  const voidMut = useMutation({
    mutationFn: () => api<PayRun>(`/api/payroll/pay-runs/${payRun.id}/void`, { method: "POST", body: { reason: voidReason } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: payrollKeys.payRun(payRun.id) }); setVoidOpen(false); },
  });

  const updateLine = (empIdx: number, lineIdx: number, field: keyof EarningLine, val: string) => {
    setEntries(prev => {
      const next = [...prev];
      next[empIdx] = {
        ...next[empIdx],
        earnings: next[empIdx].earnings.map((ln, i) => i === lineIdx ? { ...ln, [field]: val } : ln),
      };
      return next;
    });
  };

  const addLine = (empIdx: number) => {
    setEntries(prev => {
      const next = [...prev];
      next[empIdx] = { ...next[empIdx], earnings: [...next[empIdx].earnings, { code: "OT", hours: "", rate: "" }] };
      return next;
    });
  };

  const removeLine = (empIdx: number, lineIdx: number) => {
    setEntries(prev => {
      const next = [...prev];
      next[empIdx] = { ...next[empIdx], earnings: next[empIdx].earnings.filter((_, i) => i !== lineIdx) };
      return next;
    });
  };

  const isEditable = ["draft", "calculated"].includes(payRun.status);
  const canApprove = payRun.status === "calculated";
  const canVoid = !["posted", "voided"].includes(payRun.status);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Action buttons */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
        {isEditable && (
          <Button
            variant="contained"
            startIcon={calcMut.isPending ? <CircularProgress size={16} /> : <CalculateIcon />}
            onClick={() => {
              if (complianceBlocked) {
                onCalculateBlocked?.();
              } else {
                calcMut.mutate();
              }
            }}
            disabled={calcMut.isPending}
            color={complianceBlocked ? "error" : "primary"}
          >
            {calcMut.isPending ? "Calculating…" : "Calculate Taxes"}
          </Button>
        )}
        {canApprove && (
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => approveMut.mutate()}
            disabled={approveMut.isPending}
          >
            {approveMut.isPending ? "Approving…" : "Approve"}
          </Button>
        )}
        {canVoid && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<BlockIcon />}
            onClick={() => setVoidOpen(true)}
          >
            Void
          </Button>
        )}
      </Box>

      {calcMut.isError && <Alert severity="error">{errorMessage(calcMut.error)}</Alert>}
      {calcMut.isSuccess && <Alert severity="success">Pay run calculated. Review totals above and approve when ready.</Alert>}
      {approveMut.isError && (() => {
        const msg = errorMessage(approveMut.error);
        // A blocking prevailing-wage violation can only be cleared from the Compliance Queue.
        const isPwBlock = /prevailing[- ]wage|compliance|violation/i.test(msg);
        return (
          <Alert
            severity="error"
            action={isPwBlock ? (
              <Button color="inherit" size="small" onClick={() => navigate({ to: "/payroll-l4/compliance" })}>
                Open Compliance Queue
              </Button>
            ) : undefined}
          >
            {msg}
          </Alert>
        );
      })()}
      {approveMut.isSuccess && <Alert severity="success">Pay run approved. Generate ACH or post to GL from the Summary tab.</Alert>}

      {/* Per-employee earnings entry */}
      {entries.map((entry, empIdx) => (
        <Paper key={entry.employee_id} variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>{entry.employee_name}</Typography>
            {isEditable && (
              <Button size="small" startIcon={<AddIcon />} onClick={() => addLine(empIdx)}>
                Add Line
              </Button>
            )}
          </Box>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 120 }}>Code</TableCell>
                <TableCell sx={{ width: 100 }}>Hours</TableCell>
                <TableCell sx={{ width: 120 }}>Rate ($/hr)</TableCell>
                <TableCell align="right">Amount</TableCell>
                {isEditable && <TableCell sx={{ width: 40 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {entry.earnings.map((ln, lineIdx) => {
                const amount = (parseFloat(ln.hours) || 0) * (parseFloat(ln.rate) || 0);
                return (
                  <TableRow key={lineIdx}>
                    <TableCell>
                      {isEditable ? (
                        <TextField
                          size="small" value={ln.code}
                          onChange={(e) => updateLine(empIdx, lineIdx, "code", e.target.value)}
                          select sx={{ width: 100 }}
                        >
                          {(earningCodes.length > 0 ? earningCodes : [
                            { code: "REG", name: "Regular" }, { code: "OT", name: "Overtime" },
                            { code: "BONUS", name: "Bonus" }, { code: "HOL", name: "Holiday" },
                          ]).map(ec => (
                            <MenuItem key={ec.code} value={ec.code}>{ec.code}</MenuItem>
                          ))}
                        </TextField>
                      ) : ln.code}
                    </TableCell>
                    <TableCell>
                      {isEditable ? (
                        <TextField size="small" type="number" value={ln.hours}
                          onChange={(e) => updateLine(empIdx, lineIdx, "hours", e.target.value)}
                          sx={{ width: 80 }} />
                      ) : ln.hours}
                    </TableCell>
                    <TableCell>
                      {isEditable ? (
                        <MoneyField size="small" value={ln.rate}
                          onChange={(v) => updateLine(empIdx, lineIdx, "rate", v)}
                          sx={{ width: 100 }} />
                      ) : formatMoney(ln.rate)}
                    </TableCell>
                    <TableCell align="right">{formatMoney(amount)}</TableCell>
                    {isEditable && (
                      <TableCell>
                        {lineIdx > 0 && (
                          <IconButton size="small" onClick={() => removeLine(empIdx, lineIdx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      ))}

      {entries.length === 0 && (
        <Alert severity="info">No active employees found. Add employees first in Payroll → Employees.</Alert>
      )}

      {/* Void dialog */}
      <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Void Pay Run</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <TextField label="Reason (optional)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} fullWidth multiline rows={2} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => voidMut.mutate()} disabled={voidMut.isPending}>
            {voidMut.isPending ? "Voiding…" : "Void"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── AI review types ──────────────────────────────────────────────────────────

interface PaycheckAnomalyFlag {
  employee_id: string;
  employee_name: string;
  flag: "anomaly" | "info" | "ok";
  message: string;
}

interface PayRunAIReview {
  summary: string;
  overall_flag: "ok" | "warning" | "critical";
  flags: PaycheckAnomalyFlag[];
  recommendations: string[];
}

// ─── Summary tab (with ACH / Post to GL / Stubs) ─────────────────────────────

function SummaryTab({ payRun, employees }: { payRun: PayRun; employees: PayrollEmployee[] }) {
  const [achOpen, setAchOpen] = useState(false);
  const [glOpen, setGlOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiReview, setAiReview] = useState<PayRunAIReview | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const { data: paychecks = [] } = useQuery(paychecksQueryOptions(payRun.id));

  const canAch = ["approved", "paid"].includes(payRun.status);
  const canPostGL = ["approved", "paid"].includes(payRun.status) && !payRun.journal_id;
  const alreadyPosted = !!payRun.journal_id;
  const checkPaychecks = paychecks.filter((pc) => pc.payment_method === "check");
  const hasCheckPaychecks = checkPaychecks.length > 0;

  // Build name lookup
  const nameById: Record<string, string> = {};
  for (const e of employees) nameById[e.id] = `${e.first_name} ${e.last_name}`;

  // AI review mutation
  const aiMut = useMutation({
    mutationFn: () =>
      api<PayRunAIReview>(`/api/payroll/pay-runs/${payRun.id}/ai-review`, { method: "POST", body: {} }),
    onSuccess: (data) => {
      setAiReview(data);
      setAiError(null);
      setAiPanelOpen(true);
    },
    onError: (err: any) => {
      setAiError(String(err?.data?.detail ?? err?.message ?? "AI review failed"));
      setAiPanelOpen(true);
    },
  });

  const downloadStub = (pc: Paycheck) => {
    const url = `/api/payroll/paychecks/${pc.id}/stub.pdf`;
    window.open(url, "_blank");
  };

  const downloadCheckPdf = async (pc: Paycheck) => {
    const token = getToken();
    const res = await fetch(`/api/payroll/paychecks/${pc.id}/check.pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      alert(`Could not generate check PDF: ${body.detail ?? res.statusText}`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const downloadMicrCheckPdf = async (pc: Paycheck) => {
    const token = getToken();
    const res = await fetch(`/api/payroll/year-end/check/${pc.id}.pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      alert(`Could not generate MICR check PDF: ${body.detail ?? res.statusText}`);
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* GL / ACH action strip */}
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
        {/* AI review button */}
        <Tooltip title="AI anomaly detection — flags unusual gross pay, tax rates, deductions, and payment method choices">
          <Button
            variant="outlined"
            color="warning"
            startIcon={aiMut.isPending ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={() => aiMut.mutate()}
            disabled={aiMut.isPending}
            sx={{ fontWeight: 600 }}
          >
            {aiMut.isPending ? "Reviewing…" : "AI Review"}
          </Button>
        </Tooltip>

        {hasCheckPaychecks && (
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => checkPaychecks.forEach(pc => downloadCheckPdf(pc))}
          >
            Print all checks ({checkPaychecks.length})
          </Button>
        )}
        {canAch && (
          <Button
            variant="outlined"
            startIcon={<AccountBalanceIcon />}
            onClick={() => setAchOpen(true)}
          >
            Generate ACH File
          </Button>
        )}
        {canPostGL && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<PostAddIcon />}
            onClick={() => setGlOpen(true)}
          >
            Post to GL
          </Button>
        )}
        {alreadyPosted && (
          <Chip
            icon={<ReceiptLongIcon />}
            label={`Posted · Journal ${payRun.journal_id?.slice(0, 8)}…`}
            color="success"
            variant="outlined"
          />
        )}
      </Box>

      {/* AI Review panel */}
      {(aiReview || aiError) && (
        <Paper
          variant="outlined"
          sx={{
            borderColor: aiError ? "error.main" : aiReview?.overall_flag === "critical" ? "error.main" : aiReview?.overall_flag === "warning" ? "warning.main" : "success.main",
            overflow: "hidden",
          }}
        >
          <Box
            onClick={() => setAiPanelOpen(v => !v)}
            sx={{
              display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.5,
              cursor: "pointer",
              bgcolor: aiError ? "error.main" : aiReview?.overall_flag === "critical" ? "error.main" : aiReview?.overall_flag === "warning" ? "warning.main" : "success.main",
            }}
          >
            <AutoAwesomeIcon fontSize="small" sx={{ color: "background.paper" }} />
            <Typography variant="body2" fontWeight={700} sx={{ color: "background.paper", flex: 1 }}>
              {aiError
                ? `AI Error: ${aiError}`
                : `AI Review: ${aiReview?.summary}`
              }
            </Typography>
            {aiReview && (
              <Chip
                label={aiReview.overall_flag.toUpperCase()}
                size="small"
                sx={{ bgcolor: "rgba(255,255,255,0.25)", color: "background.paper", fontWeight: 700 }}
              />
            )}
            {aiPanelOpen ? <ExpandLessIcon sx={{ color: "background.paper" }} /> : <ExpandMoreIcon sx={{ color: "background.paper" }} />}
          </Box>
          <Collapse in={aiPanelOpen}>
            <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
              {aiReview && (
                <>
                  {/* Anomaly flags */}
                  {aiReview.flags.filter(f => f.flag !== "ok").length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ mb: 0.5, display: "block" }}>ANOMALIES</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Employee</TableCell>
                            <TableCell>Severity</TableCell>
                            <TableCell>Finding</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {aiReview.flags.filter(f => f.flag !== "ok").map((f, i) => (
                            <TableRow key={i}>
                              <TableCell>{f.employee_name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={f.flag.toUpperCase()}
                                  size="small"
                                  color={f.flag === "anomaly" ? "error" : "warning"}
                                />
                              </TableCell>
                              <TableCell sx={{ fontSize: "0.8rem" }}>{f.message}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                  {/* Recommendations */}
                  {aiReview.recommendations.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>RECOMMENDATIONS</Typography>
                      <List dense disablePadding>
                        {aiReview.recommendations.map((r, i) => (
                          <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                            <ListItemText
                              primary={`• ${r}`}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Run totals */}
      <Paper variant="outlined" sx={{ p: 3, maxWidth: 480 }}>
        <Typography variant="subtitle1" fontWeight={600} mb={2}>Run Totals</Typography>
        <Table size="small">
          <TableBody>
            {[
              ["Gross Wages", formatMoney(payRun.total_gross)],
              ["Employee Taxes", `(${formatMoney(payRun.total_taxes)})`],
              ["Employee Deductions", `(${formatMoney(payRun.total_deductions)})`],
              ["Net Pay", formatMoney(payRun.total_net), true],
              ["Employer Taxes & Costs", formatMoney(payRun.total_employer_costs)],
            ].map(([label, val, bold]) => (
              <TableRow key={String(label)}>
                <TableCell sx={{ border: 0, fontWeight: bold ? 600 : 400 }}>{label}</TableCell>
                <TableCell align="right" sx={{ border: 0, fontWeight: bold ? 600 : 400 }}>{val}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Paychecks */}
      {paychecks.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
            Paychecks
          </Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell align="right">Taxes</TableCell>
                  <TableCell align="right">Deductions</TableCell>
                  <TableCell align="right">Net Pay</TableCell>
                  <TableCell align="right">ACH</TableCell>
                  <TableCell align="right">Check #</TableCell>
                  <TableCell align="right">Stub</TableCell>
                  <TableCell align="right">Print check</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paychecks.map((pc) => (
                  <TableRow key={pc.id}>
                    <TableCell>{nameById[pc.employee_id] ?? pc.employee_id.slice(0, 8)}</TableCell>
                    <TableCell>
                      <Chip
                        label={pc.payment_method.toUpperCase()}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">{formatMoney(pc.gross_wages)}</TableCell>
                    <TableCell align="right" sx={{ color: "text.secondary" }}>{formatMoney(pc.total_ee_taxes)}</TableCell>
                    <TableCell align="right" sx={{ color: "text.secondary" }}>{formatMoney(pc.total_deductions)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(pc.net_pay)}</TableCell>
                    <TableCell align="right">
                      {pc.ach_status ? (
                        <Chip
                          label={pc.ach_status}
                          size="small"
                          color={pc.ach_status === "sent" ? "success" : pc.ach_status === "returned" ? "error" : "default"}
                          variant="outlined"
                        />
                      ) : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary" }}>
                      {(pc as any).check_number ?? "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Download pay stub PDF">
                        <IconButton size="small" onClick={() => downloadStub(pc)}>
                          <PictureAsPdfIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      {pc.payment_method === "check" ? (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Print check PDF (basic)">
                            <IconButton size="small" color="secondary" onClick={() => downloadCheckPdf(pc)}>
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print MICR check (3-up: check + 2 stubs, MICR-encoded routing)">
                            <IconButton size="small" color="primary" onClick={() => downloadMicrCheckPdf(pc)}>
                              <ReceiptLongIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      )}

      {payRun.notes && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">Notes</Typography>
          <Typography variant="body2">{payRun.notes}</Typography>
        </Paper>
      )}

      <AchDialog open={achOpen} onClose={() => setAchOpen(false)} payRunId={payRun.id} />
      <PostToGLDialog open={glOpen} onClose={() => setGlOpen(false)} payRunId={payRun.id} />
    </Box>
  );
}

// ─── L2: Burden tab ──────────────────────────────────────────────────────────

function L2BurdenTab({ payRunId }: { payRunId: string }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["payroll-l2", "burden-variance", payRunId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/pay-runs/${payRunId}/burden/variance`);
      return res as BurdenVarianceRow[];
    },
  });
  if (isLoading) return <CircularProgress size={20} />;
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>Burden Variance</Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Component</TableCell>
              <TableCell>Craft</TableCell>
              <TableCell align="right">Estimated %</TableCell>
              <TableCell align="right">Estimated $</TableCell>
              <TableCell align="right">Actual $</TableCell>
              <TableCell align="right">Variance $</TableCell>
              <TableCell align="right">Variance %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, i) => {
              const varPct = parseFloat(row.estimated) > 0
                ? ((parseFloat(row.actual) - parseFloat(row.estimated)) / parseFloat(row.estimated)) * 100
                : 0;
              const isHigh = Math.abs(varPct) > 10;
              return (
                <TableRow key={i} sx={isHigh ? { bgcolor: "rgba(239,68,68,0.05)" } : {}}>
                  <TableCell>{row.component_name}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>—</TableCell>
                  <TableCell align="right">—</TableCell>
                  <TableCell align="right">{formatMoney(row.estimated)}</TableCell>
                  <TableCell align="right">{formatMoney(row.actual)}</TableCell>
                  <TableCell align="right" sx={{ color: isHigh ? "error.main" : "inherit" }}>
                    {formatMoney(row.variance)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: isHigh ? "error.main" : "inherit", fontWeight: isHigh ? 700 : 400 }}>
                    {varPct >= 0 ? "+" : ""}{varPct.toFixed(1)}%
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

// ─── L2: OT Audit tab ────────────────────────────────────────────────────────

function L2OTAuditTab({ payRunId }: { payRunId: string }) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const { data = [], isLoading } = useQuery({
    queryKey: ["payroll-l2", "ot-calculation-log", payRunId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/pay-runs/${payRunId}/ot-log`);
      return res as OTCalculationLog[];
    },
  });
  if (isLoading) return <CircularProgress size={20} />;
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>OT Audit Log</Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Rule Set</TableCell>
              <TableCell align="right">Reg Hrs</TableCell>
              <TableCell align="right">OT Hrs</TableCell>
              <TableCell align="right">DT Hrs</TableCell>
              <TableCell align="right">Reg Rate</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <>
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setExpanded(expanded === row.id ? null : row.id)}
                >
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.employee_id.slice(0, 8)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{row.period_start} – {row.period_end}</TableCell>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.rule_set_id?.slice(0, 8) ?? "—"}</TableCell>
                  <TableCell align="right">{row.regular_hours}</TableCell>
                  <TableCell align="right" sx={{ color: parseFloat(row.ot_hours) > 0 ? "warning.main" : "inherit" }}>{row.ot_hours}</TableCell>
                  <TableCell align="right" sx={{ color: parseFloat(row.dt_hours) > 0 ? "error.main" : "inherit" }}>{row.dt_hours}</TableCell>
                  <TableCell align="right">{row.regular_rate ? formatMoney(row.regular_rate) : "—"}</TableCell>
                  <TableCell>
                    {expanded === row.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </TableCell>
                </TableRow>
                {expanded === row.id && row.reasoning && (
                  <TableRow key={`${row.id}-detail`}>
                    <TableCell colSpan={8} sx={{ bgcolor: "action.hover", p: 2 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>
                        CALCULATION REASONING
                      </Typography>
                      {typeof row.reasoning === "string" ? (
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{row.reasoning}</Typography>
                      ) : (
                        <List dense disablePadding>
                          {(Array.isArray(row.reasoning) ? row.reasoning : Object.entries(row.reasoning as Record<string, unknown>).map(([k, v]) => `${k}: ${v}`)).map((step: unknown, i: number) => (
                            <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                              <ListItemText
                                primary={`${i + 1}. ${String(step)}`}
                                primaryTypographyProps={{ variant: "body2" }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

// ─── L2: Allowances tab ───────────────────────────────────────────────────────

function L2AllowancesTab({ payRunId }: { payRunId: string }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["payroll-l2", "allowance-entries", payRunId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/allowance-entries?pay_run_id=${payRunId}`);
      return res as AllowanceEntry[];
    },
  });
  if (isLoading) return <CircularProgress size={20} />;
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>Allowance Entries</Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Units</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Taxable</TableCell>
              <TableCell align="right">Non-taxable</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => {
              const amt = parseFloat(row.amount);
              // Simplified: per_diem and mileage are non-taxable by default
              const taxable = ["phone", "tool", "uniform"].includes(row.allowance_type) ? amt : 0;
              const nonTaxable = amt - taxable;
              return (
                <TableRow key={row.id}>
                  <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.employee_id.slice(0, 8)}</TableCell>
                  <TableCell>{row.entry_date}</TableCell>
                  <TableCell><Chip label={row.allowance_type} size="small" variant="outlined" /></TableCell>
                  <TableCell align="right">{row.quantity}</TableCell>
                  <TableCell align="right">{formatMoney(row.rate)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(row.amount)}</TableCell>
                  <TableCell align="right">{taxable > 0 ? formatMoney(taxable) : "—"}</TableCell>
                  <TableCell align="right">{nonTaxable > 0 ? formatMoney(nonTaxable) : "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

// ─── L2: Compliance tab ───────────────────────────────────────────────────────

function L2ComplianceTab({ payRunId }: { payRunId: string }) {
  const qc = useQueryClient();
  const [overrideTarget, setOverrideTarget] = useState<ComplianceGateResult | null>(null);
  const [overrideReason, setOverrideReason] = useState("");

  const { data = [], isLoading } = useQuery({
    queryKey: ["payroll-l2", "compliance-gate", payRunId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/compliance-gate/${payRunId}`);
      return res as ComplianceGateResult[];
    },
  });

  const overrideMut = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      api(`/api/payroll-l2/compliance-gate/${payRunId}/override/${id}`, { method: "POST", body: { reason } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["payroll-l2", "compliance-gate", payRunId] });
      setOverrideTarget(null);
      setOverrideReason("");
    },
  });

  if (isLoading) return <CircularProgress size={20} />;

  const blockCount = data.filter((r) => r.blocking && r.result !== "pass").length;
  const allPass = data.length > 0 && data.every((r) => r.result === "pass");

  return (
    <Box>
      {blockCount > 0 && (
        <Alert severity="error" icon={<BlockIcon />} sx={{ mb: 2 }}>
          <strong>Pay run is blocked</strong> — resolve all compliance issues before calculating.
        </Alert>
      )}
      {allPass && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>All compliance checks passed.</Alert>
      )}

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {data.map((item) => (
          <Paper
            key={item.id}
            variant="outlined"
            sx={{
              p: 2,
              borderColor: item.blocking && item.result !== "pass"
                ? "error.main"
                : item.result !== "pass"
                ? "warning.main"
                : "success.main",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {item.result === "pass"
              ? <CheckCircleIcon color="success" fontSize="small" />
              : item.blocking
              ? <BlockIcon color="error" fontSize="small" />
              : <ExpandMoreIcon color="warning" fontSize="small" />
            }
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={600}>{item.check_name}</Typography>
              {item.message && <Typography variant="caption" color="text.secondary">{item.message}</Typography>}
            </Box>
            {item.result === "pass" ? (
              <Chip label="Pass" color="success" size="small" />
            ) : item.blocking ? (
              <Chip label="Block" color="error" size="small" />
            ) : (
              <Chip label="Warn" color="warning" size="small" />
            )}
            {item.blocking && item.result !== "pass" && (
              <Button variant="outlined" color="warning" size="small" onClick={() => setOverrideTarget(item)}>
                Override
              </Button>
            )}
          </Paper>
        ))}
      </Box>

      <Dialog open={!!overrideTarget} onClose={() => setOverrideTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Override Compliance Block</DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          <TextField
            autoFocus fullWidth multiline rows={3}
            label="Business Reason (required)"
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOverrideTarget(null)}>Cancel</Button>
          <Button
            variant="contained" color="warning"
            disabled={!overrideReason.trim() || overrideMut.isPending}
            onClick={() => overrideTarget && overrideMut.mutate({ id: overrideTarget.id, reason: overrideReason })}
          >
            {overrideMut.isPending ? "Saving…" : "Override"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── L2: EV tab ──────────────────────────────────────────────────────────────

function L2EVTab({ payRunId }: { payRunId: string }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["payroll-l2", "earned-value", payRunId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/earned-value?pay_run_id=${payRunId}`);
      return res as EarnedValueSnapshot[];
    },
  });
  if (isLoading) return <CircularProgress size={20} />;

  function spiCpiColor(val: string | null) {
    if (!val) return "inherit";
    const n = parseFloat(val);
    if (n >= 1.0) return "success.main";
    if (n >= 0.8) return "warning.main";
    return "error.main";
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>Earned Value</Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Project</TableCell>
              <TableCell>Snapshot</TableCell>
              <TableCell align="right">Budgeted Hrs</TableCell>
              <TableCell align="right">Actual Hrs</TableCell>
              <TableCell align="right">Budgeted Cost</TableCell>
              <TableCell align="right">Actual Cost</TableCell>
              <TableCell align="right">SPI</TableCell>
              <TableCell align="right">CPI</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id}>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{row.project_id.slice(0, 8)}</TableCell>
                <TableCell>{row.snapshot_date}</TableCell>
                <TableCell align="right">{row.budgeted_hours ?? "—"}</TableCell>
                <TableCell align="right">{row.actual_hours ?? "—"}</TableCell>
                <TableCell align="right">{row.budgeted_cost ? formatMoney(row.budgeted_cost) : "—"}</TableCell>
                <TableCell align="right">{row.actual_cost ? formatMoney(row.actual_cost) : "—"}</TableCell>
                <TableCell align="right" sx={{ color: spiCpiColor(row.spi), fontWeight: 700 }}>
                  {row.spi ?? "—"}
                </TableCell>
                <TableCell align="right" sx={{ color: spiCpiColor(row.cpi), fontWeight: 700 }}>
                  {row.cpi ?? "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

// ─── L2: Anomalies tab ────────────────────────────────────────────────────────

function L2AnomaliesTab({ payRunId }: { payRunId: string }) {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["payroll-l2", "ai-anomalies", payRunId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/ai/anomalies?pay_run_id=${payRunId}`);
      return res as AiLaborAnomaly[];
    },
  });

  const ackMut = useMutation({
    mutationFn: async (anomalyId: string) =>
      api(`/api/payroll-l2/ai/anomalies/${anomalyId}/acknowledge`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payroll-l2", "ai-anomalies", payRunId] }),
  });

  if (isLoading) return <CircularProgress size={20} />;

  function severityColor(s: string): "default" | "info" | "warning" | "error" {
    if (s === "critical") return "error";
    if (s === "warn") return "warning";
    return "info";
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>AI Anomalies</Typography>
      {data.length === 0 && (
        <Alert severity="success" icon={<CheckCircleIcon />}>No anomalies detected for this pay run.</Alert>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {data.map((anomaly) => (
          <Paper
            key={anomaly.id}
            variant="outlined"
            sx={{
              p: 2,
              borderColor: anomaly.severity === "critical"
                ? "error.main"
                : anomaly.severity === "warn"
                ? "warning.main"
                : "info.main",
              opacity: anomaly.acknowledged ? 0.6 : 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle2" fontWeight={600}>{anomaly.metric}</Typography>
                  <Chip label={anomaly.severity} color={severityColor(anomaly.severity)} size="small" />
                  {anomaly.acknowledged && <Chip label="Acknowledged" size="small" variant="outlined" />}
                </Box>
                <Box sx={{ display: "flex", gap: 3, mb: 1 }}>
                  {anomaly.observed_value && (
                    <Typography variant="body2" color="text.secondary">
                      Observed: <strong>{anomaly.observed_value}</strong>
                    </Typography>
                  )}
                  {anomaly.expected_value && (
                    <Typography variant="body2" color="text.secondary">
                      Expected: <strong>{anomaly.expected_value}</strong>
                    </Typography>
                  )}
                </Box>
                {anomaly.explanation && (
                  <Typography variant="body2">{anomaly.explanation}</Typography>
                )}
              </Box>
              {!anomaly.acknowledged && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => ackMut.mutate(anomaly.id)}
                  disabled={ackMut.isPending}
                >
                  Acknowledge
                </Button>
              )}
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

// ─── L2: Equipment tab ────────────────────────────────────────────────────────

interface EquipmentTimeEntry {
  id: string;
  equipment_id: string;
  equipment_name: string;
  project_id: string | null;
  run_hours: string;
  idle_hours: string;
  transport_hours: string;
  total_cost: string;
}

function L2EquipmentTab({ payRunId }: { payRunId: string }) {
  const { data = [], isLoading } = useQuery({
    queryKey: ["payroll-l2", "equipment-time", payRunId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/equipment-time?pay_run_id=${payRunId}`);
      return res as EquipmentTimeEntry[];
    },
  });
  if (isLoading) return <CircularProgress size={20} />;
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} mb={2}>Equipment Time</Typography>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Equipment</TableCell>
              <TableCell>Project</TableCell>
              <TableCell align="right">Run Hrs</TableCell>
              <TableCell align="right">Idle Hrs</TableCell>
              <TableCell align="right">Transport Hrs</TableCell>
              <TableCell align="right">Total Cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.equipment_name}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                  {row.project_id ? row.project_id.slice(0, 8) : "—"}
                </TableCell>
                <TableCell align="right">{row.run_hours}</TableCell>
                <TableCell align="right">{row.idle_hours}</TableCell>
                <TableCell align="right">{row.transport_hours}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(row.total_cost)}</TableCell>
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary" }}>
                  No equipment time entries for this pay run.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

// ─── Audit Trail tab ──────────────────────────────────────────────────────────

interface AuditEvent {
  at: string;
  action: string;
  table: string;
  by: string | null;
  channel: string;
  is_agent: boolean;
  summary: string;
}

function AuditTrailTab({ payRunId }: { payRunId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll", "audit-trail", payRunId],
    queryFn: () => api<{ events: AuditEvent[]; event_count: number }>(
      `/api/payroll/pay-runs/${payRunId}/audit-trail`,
    ),
  });

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{errorMessage(error)}</Alert>;
  const events = data?.events ?? [];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every recorded action on this pay run — who did it, when, and whether it
        was done in the app or by the Payroll Clerk agent on a user's
        authorization. This is the audit trail for compliance and Davis-Bacon
        certified payroll.
      </Typography>
      {events.length === 0 ? (
        <Alert severity="info">No recorded activity yet for this pay run.</Alert>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 170 }}>When</TableCell>
                <TableCell sx={{ width: 110 }}>Action</TableCell>
                <TableCell>What happened</TableCell>
                <TableCell sx={{ width: 200 }}>By</TableCell>
                <TableCell sx={{ width: 150 }}>How</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((e, i) => (
                <TableRow key={i}>
                  <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                    {new Date(e.at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={e.action}
                      color={["APPROVE", "SIGN"].includes(e.action) ? "success"
                        : ["CURE", "GENERATE"].includes(e.action) ? "info"
                        : e.action === "DELETE" ? "error" : "default"}
                      variant={["INSERT", "UPDATE", "DELETE"].includes(e.action) ? "outlined" : "filled"}
                    />
                  </TableCell>
                  <TableCell>{e.summary}</TableCell>
                  <TableCell>{e.by ?? "System"}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      color={e.is_agent ? "secondary" : "default"}
                      icon={e.is_agent ? <AutoAwesomeIcon sx={{ fontSize: 14 }} /> : undefined}
                      label={e.channel}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

// Tab indices as const
const BASE_TABS = ["Earnings Entry", "Summary & Actions"] as const;
const L2_TABS = ["Burden", "OT Audit", "Allowances", "Compliance", "EV", "Anomalies", "Equipment"] as const;

function PayRunDetailPage() {
  const { payRunId } = Route.useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [complianceBlockDialog, setComplianceBlockDialog] = useState(false);

  const { data: payRun, isLoading } = useQuery(payRunQueryOptions(payRunId));
  const { data: employees = [] } = useQuery(payrollEmployeesQueryOptions(true));
  const { data: earningCodes = [] } = useQuery(earningCodesQueryOptions());
  const entitiesQuery = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQuery.data?.find((e) => e.is_current);
  const constructionEnabled = currentEntity?.construction_enabled ?? false;

  // Compliance gate check for Calculate button
  const { data: complianceResults = [] } = useQuery({
    queryKey: ["payroll-l2", "compliance-gate", payRunId],
    queryFn: async () => {
      const res = await api(`/api/payroll-l2/compliance-gate/${payRunId}`);
      return res as ComplianceGateResult[];
    },
    enabled: constructionEnabled,
  });
  const hasComplianceBlocks = complianceResults.some((r) => r.blocking && r.result !== "pass");

  const allTabs = constructionEnabled
    ? [...BASE_TABS, ...L2_TABS, "Audit Trail"]
    : [...BASE_TABS, "Audit Trail"];
  const auditTabIdx = allTabs.length - 1;

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;
  if (!payRun) return <Box sx={{ p: 3 }}><Alert severity="error">Pay run not found</Alert></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate({ to: "/payroll/pay-runs" })} sx={{ mb: 2 }}>
        Pay Runs
      </Button>

      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Pay Run {payRun.pay_run_number}
          </Typography>
          <Chip
            label={STATUS_LABEL[payRun.status as PayRunStatus] ?? payRun.status}
            color={STATUS_COLOR[payRun.status as PayRunStatus] ?? "default"}
            size="small"
          />
          {payRun.journal_id && (
            <Chip
              label="GL Posted"
              color="success"
              size="small"
              icon={<ReceiptLongIcon />}
              variant="outlined"
            />
          )}
        </Box>
        <HelpHint docPath="payroll-l1/pay-run-lifecycle.md" label="Pay run help" />
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Period: {fmtDate(payRun.period_start)} – {fmtDate(payRun.period_end)} · Pay Date: {fmtDate(payRun.pay_date)}
      </Typography>

      {/* Compliance block banner (L2 only) */}
      {constructionEnabled && hasComplianceBlocks && (
        <Alert severity="error" icon={<BlockIcon />} sx={{ mb: 2 }}>
          <strong>Pay run is blocked</strong> — resolve all compliance issues before calculating.
          {" "}
          <Button
            size="small"
            color="inherit"
            onClick={() => navigate({
              to: "/payroll-l2/compliance-gate/$payRunId",
              params: { payRunId: payRun.id },
            })}
          >
            View Compliance Gate
          </Button>
        </Alert>
      )}

      {/* Stat cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <StatCard label="Gross Wages" value={formatMoney(payRun.total_gross)} />
        <StatCard label="EE Taxes" value={formatMoney(payRun.total_taxes)} />
        <StatCard label="Deductions" value={formatMoney(payRun.total_deductions)} />
        <StatCard label="Net Pay" value={formatMoney(payRun.total_net)} color="success.main" />
        <StatCard label="ER Cost" value={formatMoney(payRun.total_employer_costs)} />
      </Box>

      {/* Run Certified Payroll shortcut — only shown when the pack is on and the run is still open */}
      {currentEntity?.certified_payroll_enabled && ["draft", "calculated"].includes(payRun.status) && (
        <Box sx={{ mb: 2 }}>
          <Tooltip title="Open Ask Bynaus and pre-fill the certified payroll command for this pay run">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => {
                const prompt = `Run certified payroll for pay run ${payRun.pay_run_number} (week ending ${payRun.period_end})`;
                window.dispatchEvent(
                  new CustomEvent("bynaus:open-agent", { detail: { prompt, autoSend: false } }),
                );
              }}
            >
              Run Certified Payroll
            </Button>
          </Tooltip>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {allTabs.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {tab === 0 && (
        <EarningsEntryTab
          payRun={payRun}
          employees={employees as PayrollEmployee[]}
          earningCodes={earningCodes as { id: string; code: string; name: string }[]}
          complianceBlocked={constructionEnabled && hasComplianceBlocks}
          onCalculateBlocked={() => setComplianceBlockDialog(true)}
        />
      )}
      {tab === 1 && (
        <SummaryTab
          payRun={payRun}
          employees={employees as PayrollEmployee[]}
        />
      )}
      {constructionEnabled && tab === 2 && <L2BurdenTab payRunId={payRunId} />}
      {constructionEnabled && tab === 3 && <L2OTAuditTab payRunId={payRunId} />}
      {constructionEnabled && tab === 4 && <L2AllowancesTab payRunId={payRunId} />}
      {constructionEnabled && tab === 5 && <L2ComplianceTab payRunId={payRunId} />}
      {constructionEnabled && tab === 6 && <L2EVTab payRunId={payRunId} />}
      {constructionEnabled && tab === 7 && <L2AnomaliesTab payRunId={payRunId} />}
      {constructionEnabled && tab === 8 && <L2EquipmentTab payRunId={payRunId} />}
      {tab === auditTabIdx && <AuditTrailTab payRunId={payRunId} />}

      {/* Compliance block dialog */}
      <Dialog open={complianceBlockDialog} onClose={() => setComplianceBlockDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "error.main" }}>
          <BlockIcon sx={{ verticalAlign: "middle", mr: 1 }} />
          Pay Run Blocked
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This pay run has {complianceResults.filter((r) => r.blocking && r.result !== "pass").length} blocking compliance issue(s).
            Calculation is not allowed until all blocks are resolved.
          </Alert>
          <Typography variant="body2" mb={1}>Blocking issues:</Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {complianceResults
              .filter((r) => r.blocking && r.result !== "pass")
              .map((r) => (
                <Typography key={r.id} variant="body2" color="error.main">
                  • {r.check_name}: {r.message}
                </Typography>
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComplianceBlockDialog(false)}>Close</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setComplianceBlockDialog(false);
              setTab(constructionEnabled ? 5 : 0); // jump to compliance tab
            }}
          >
            Go to Compliance Tab
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
